/**
 * @file HaulageRateImportModal.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-29
 * @modified 2026-09-29
 * @description Read a supplier's haulage rate card — screenshot, PDF,
 *              spreadsheet, Word document or pasted text — and add its rates to
 *              the supplier's Haulage rates after a row-by-row review.
 *
 *              Every row sits beside the rate already on file for that
 *              postcode, so a rise is visible as a rise. Nothing is saved until
 *              the reviewer says so, and only ticked rows are. A row that is not
 *              a postcode ("Devon") or has no rate starts unticked and says why.
 *
 *              UNITS. The card stores £ per pallet. A rate the card gives per
 *              tonne or per load is converted, on assumptions every row shows
 *              and anyone can change: 1 T per pallet (40 × 25kg), 26 pallets
 *              per load. A rate the card does not qualify is taken as per
 *              pallet. Rows can be ticked and changed together — bulk bar or
 *              right-click — as well as one at a time.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import Input from './Input';
import DateInput from './DateInput';
import TickCheckbox from './TickCheckbox';
import ImportDropZone from './ImportDropZone';
import SegToggle from './SegToggle';
import TableContextMenu from './TableContextMenu';
import useTableContextMenu from '../hooks/useTableContextMenu';
import { useSlideOutButton } from './SlideOutButton';
import { formatPrice } from '../utils/currencyFormatter';
import { formatDateShort } from '../utils/dateFormatters';
import './HaulageRateImportModal.css';

const CONFIDENCE_COPY = {
  high: { icon: 'check_circle', text: 'Read cleanly' },
  medium: { icon: 'info', text: 'Check the figures before saving' },
  low: { icon: 'warning', text: 'Low confidence — check every figure carefully' }
};

const BASIS_COPY = {
  per_pallet: null,
  unknown: 'The card does not say what this is per — taken as per pallet',
  per_tonne: null,
  per_load: null,
  other: 'Priced some other way on the card — check the note, then set the rate by hand'
};

// The assumptions a per-tonne or per-load rate is converted on. Editable per
// row and in bulk; these are only where they start.
const DEFAULT_T_PER_PALLET = 1;
const DEFAULT_PALLETS_PER_LOAD = 26;
const RATE_IS_OPTIONS = [
  { value: 'pallet', label: 'pallet' },
  { value: 'tonne', label: 'T' },
  { value: 'load', label: 'load' }
];
const rateIsFor = (basis) => (basis === 'per_tonne' ? 'tonne' : basis === 'per_load' ? 'load' : 'pallet');

/** £ per pallet from the card's rate and the row's assumptions; '' if it can't be worked out. */
function perPallet(row) {
  const r = Number(row.rate_read);
  if (row.rate_read === null || row.rate_read === '' || !Number.isFinite(r)) return '';
  let v = r;
  if (row.rate_is === 'tonne') {
    const t = Number(row.t_per_pallet);
    if (!(t > 0)) return '';
    v = r * t;
  } else if (row.rate_is === 'load') {
    const p = Number(row.pallets_per_load);
    if (!(p > 0)) return '';
    v = r / p;
  }
  return (Math.round(v * 100) / 100).toFixed(2);
}

const PREFIX = /^[A-Z]{1,2}([0-9][0-9A-Z]?)?$/;
const today = () => new Date().toISOString().slice(0, 10);
const cleanPrefix = (v) => String(v || '').toUpperCase().replace(/\s+/g, '');

function ReviewBody({
  stage, error, preview, pasteText, setPasteText, onFile, onText, onError,
  result, rows, updateRow, updateRows, from, setFrom, duplicates, selected, setSelected
}) {
  useSlideOutButton([stage, rows.length, selected.size]);
  const [bulkT, setBulkT] = useState('');
  const [bulkLoad, setBulkLoad] = useState('');
  const ctxRows = useMemo(() => rows.map((r, i) => ({ ...r, _i: i })), [rows]);
  const ctx = useTableContextMenu({ rows: ctxRows, selectedIds: selected, getRowId: (r) => r._i });
  const actionsFor = (ids) => [
    { key: 'per-pallet', icon: 'pallet', label: 'Card rate is per pallet', onClick: () => updateRows(ids, { rate_is: 'pallet' }) },
    { key: 'per-tonne', icon: 'scale', label: 'Card rate is per tonne', onClick: () => updateRows(ids, { rate_is: 'tonne' }) },
    { key: 'per-load', icon: 'local_shipping', label: 'Card rate is per load', onClick: () => updateRows(ids, { rate_is: 'load' }) },
    { key: 'use', icon: 'check_box', label: 'Save', onClick: () => updateRows(ids, { use: true }) },
    { key: 'skip', icon: 'check_box_outline_blank', label: "Don't save", onClick: () => updateRows(ids, { use: false }) }
  ];

  if (stage !== 'review') {
    return (
      <ImportDropZone
        busy={stage === 'reading'}
        error={error}
        preview={preview}
        pasteText={pasteText}
        setPasteText={setPasteText}
        onFile={onFile}
        onText={onText}
        onError={onError}
        title="Drop the supplier's rate card here, or click to choose one"
        busyTitle="Reading the rate card…"
        pasteLabel="Or paste the rates as text"
        pastePlaceholder={'Copy the rates from an email or a spreadsheet and paste them here, e.g.\nEX  £18.50 per pallet\nTR, PL  £21.00\nEX31  £19.00'}
      />
    );
  }

  const conf = CONFIDENCE_COPY[result.confidence] || CONFIDENCE_COPY.medium;
  const chosen = rows.filter(r => r.use).length;

  return (
    <div className="hri-review">
      <div className="hri-main">
        {error && <div className="hri-error">{error}</div>}
        {selected.size > 0 && (
          <div className="import-bulk-bar">
            <span className="import-bulk-count">{selected.size} selected</span>
            <span className="import-bulk-group">Card rate per
              <SegToggle value={null} options={RATE_IS_OPTIONS} onChange={(v) => updateRows([...selected], { rate_is: v })} />
            </span>
            <span className="import-bulk-group">T / pallet
              <input className="import-mini-input" type="number" min="0" step="0.05" value={bulkT} onChange={(e) => setBulkT(e.target.value)} />
              <button type="button" className="import-mini-btn" disabled={!(Number(bulkT) > 0)}
                onClick={() => updateRows([...selected], { t_per_pallet: bulkT })}>Set</button>
            </span>
            <span className="import-bulk-group">Pallets / load
              <input className="import-mini-input" type="number" min="1" value={bulkLoad} onChange={(e) => setBulkLoad(e.target.value)} />
              <button type="button" className="import-mini-btn" disabled={!(Number(bulkLoad) > 0)}
                onClick={() => updateRows([...selected], { pallets_per_load: bulkLoad })}>Set</button>
            </span>
            <button type="button" className="import-mini-btn" onClick={() => updateRows([...selected], { use: true })}>Save</button>
            <button type="button" className="import-mini-btn" onClick={() => updateRows([...selected], { use: false })}>Don't save</button>
            <button type="button" className="import-mini-btn" onClick={() => setSelected(new Set())}>Clear</button>
          </div>
        )}
        <table className="data-table hri-table" onContextMenu={ctx.onTableContextMenu}>
          <thead>
            <tr>
              <th className="hri-sel">
                <TickCheckbox
                  id="hri-select-all"
                  checked={rows.length > 0 && selected.size === rows.length}
                  ariaLabel="Select every row"
                  onChange={() => setSelected(selected.size === rows.length ? new Set() : new Set(rows.map((_, i) => i)))}
                />
              </th>
              <th className="hri-use">Use</th>
              <th>On the card</th>
              <th>Postcode</th>
              <th className="hri-units">Card rate</th>
              <th className="num">£ / pallet</th>
              <th>On file now</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const prefixOk = PREFIX.test(row.postcode);
              const rateOk = row.rate !== '' && Number.isFinite(Number(row.rate)) && Number(row.rate) >= 0;
              const dup = row.use && duplicates.has(row.postcode);
              const basisNote = BASIS_COPY[row.basis];
              const cur = row.current;
              const change = cur && rateOk && cur.rate > 0 ? (Number(row.rate) - cur.rate) / cur.rate : null;
              return (
                <tr key={i} className={`${row.use ? '' : 'unused'}${dup ? ' duplicate' : ''}${selected.has(i) ? ' selected' : ''}`}
                  onContextMenu={ctx.onRowContextMenu(ctxRows[i])}>
                  <td className="hri-sel">
                    <TickCheckbox
                      id={`hri-sel-${i}`}
                      checked={selected.has(i)}
                      ariaLabel={`Select ${row.printed || row.postcode}`}
                      onChange={() => setSelected(prev => { const n = new Set(prev); if (n.has(i)) n.delete(i); else n.add(i); return n; })}
                    />
                  </td>
                  <td className="hri-use">
                    <TickCheckbox
                      id={`hri-use-${i}`}
                      checked={row.use}
                      disabled={!prefixOk || !rateOk}
                      ariaLabel={`Save the ${row.postcode || row.printed} rate`}
                      onChange={() => updateRow(i, { use: !row.use })}
                    />
                  </td>
                  <td className="hri-printed">
                    <div>{row.printed || '—'}</div>
                    {(row.note || basisNote) && (
                      <div className={`hri-note${basisNote && row.basis !== 'unknown' ? ' warn' : ''}`}>
                        {[basisNote, row.note].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </td>
                  <td className="hri-postcode">
                    <Input
                      value={row.postcode}
                      onChange={(e) => updateRow(i, { postcode: cleanPrefix(e.target.value) })}
                      placeholder="EX or EX31"
                      maxLength={5}
                    />
                    {!prefixOk && <div className="hri-note warn">Not a postcode area or district{row.postcode_as_read ? ` (read "${row.postcode_as_read}")` : ''}</div>}
                    {dup && <div className="hri-note error">Also on another ticked row</div>}
                  </td>
                  <td className="hri-units">
                    <div className="hri-units-row">
                      <span>{row.rate_read != null && row.rate_read !== '' ? formatPrice(row.rate_read) : '—'} per</span>
                      <SegToggle value={row.rate_is} options={RATE_IS_OPTIONS} onChange={(v) => updateRow(i, { rate_is: v })}
                        title="What the card's rate is per" />
                    </div>
                    {row.rate_is === 'tonne' && (
                      <div className="hri-units-row">
                        <input className="import-mini-input" type="number" min="0" step="0.05" value={row.t_per_pallet}
                          onChange={(e) => updateRow(i, { t_per_pallet: e.target.value })} /> T per pallet
                      </div>
                    )}
                    {row.rate_is === 'load' && (
                      <div className="hri-units-row">
                        <input className="import-mini-input" type="number" min="1" value={row.pallets_per_load}
                          onChange={(e) => updateRow(i, { pallets_per_load: e.target.value })} /> pallets per load
                      </div>
                    )}
                  </td>
                  <td className="num hri-rate">
                    <Input
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      value={row.rate}
                      onChange={(e) => updateRow(i, { rate: e.target.value })}
                      placeholder="0.00"
                    />
                  </td>
                  <td className="hri-current">
                    {cur ? (
                      <>
                        {formatPrice(cur.rate)} <span className="muted">from {formatDateShort(cur.effective_from)}{cur.in_force ? '' : ' (not in force)'}</span>
                        {change !== null && Math.abs(change) >= 0.0005 && (
                          <span className={`hri-move ${change > 0 ? 'up' : 'down'}`}>
                            {change > 0 ? '+' : ''}{(change * 100).toFixed(1)}%
                          </span>
                        )}
                      </>
                    ) : <span className="muted">New</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <TableContextMenu
          ctx={ctx}
          rowActions={(r) => actionsFor([r._i])}
          bulkActions={(ids) => actionsFor([...ids]).map(a => ({ ...a, label: `${a.label} (${ids.size} rows)` }))}
        />
        <p className="hri-summary">
          {chosen} of {rows.length} row{rows.length === 1 ? '' : 's'} will be saved, each from {from ? formatDateShort(from) : 'today'}.
          A rate already on file for the same postcode and the same start date is replaced; otherwise the old rate stays,
          and the new one takes over from its start date.
        </p>
      </div>

      <aside className="hri-aside">
        <div className={`hri-verdict conf-${result.confidence}`}>
          <div className="hri-verdict-head">
            <span className="material-symbols-rounded">{conf.icon}</span>
            <strong>{conf.text}</strong>
          </div>
          <div className="hri-verdict-note">
            {rows.length} row{rows.length === 1 ? '' : 's'} read{result.supplier ? ` from ${result.supplier}` : ''}.
            {result.notes ? ` ${result.notes}` : ''}
          </div>
        </div>
        <div className="hri-from">
          <DateInput label="Rates apply from" value={from} onChange={(e) => setFrom(e.target.value)} />
          <span className="hri-from-hint">
            {result.effective_from ? 'Taken from the card — change it if that is wrong.' : 'The card gives no date, so today.'}
          </span>
        </div>
        {preview && (
          <div className="hri-preview"><img src={preview} alt="The rate card that was read" /></div>
        )}
      </aside>
    </div>
  );
}

export default function HaulageRateImportModal({ supplierId, supplierName, onClose, onSaved }) {
  const [stage, setStage] = useState('pick'); // pick | reading | review
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [pasteText, setPasteText] = useState('');
  const [result, setResult] = useState(null);
  const [rows, setRows] = useState([]);
  const [from, setFrom] = useState(today());
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(() => new Set());

  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const read = useCallback(async (request) => {
    setError('');
    setStage('reading');
    try {
      const res = await fetch(`${API_URL}/api/supplier-haulage/suppliers/${supplierId}/rates/extract`, {
        method: 'POST', credentials: 'include', ...request
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not read that rate card');
      if (!(data.rows || []).length) {
        setError('No rates were found. Try a clearer or closer crop, or paste the rates as text.');
        setStage('pick');
        return;
      }
      setResult(data);
      setFrom(data.effective_from || today());
      setSelected(new Set());
      setRows(data.rows.map(r => {
        const prefix = r.postcode || '';
        const row = {
          ...r,
          postcode: prefix,
          rate_read: r.rate,
          rate_is: rateIsFor(r.basis),
          t_per_pallet: String(DEFAULT_T_PER_PALLET),
          pallets_per_load: String(DEFAULT_PALLETS_PER_LOAD)
        };
        row.rate = perPallet(row);
        // A real postcode with a rate starts ticked; one priced some other way
        // than pallet / tonne / load needs setting by hand first.
        row.use = PREFIX.test(prefix) && row.rate !== '' && r.basis !== 'other';
        return row;
      }));
      setStage('review');
    } catch (e) {
      setError(e.message);
      setStage('pick');
    }
  }, [supplierId]);

  const onFile = useCallback((file) => {
    if (!file) return;
    setPreview((old) => { if (old) URL.revokeObjectURL(old); return file.type.startsWith('image/') ? URL.createObjectURL(file) : null; });
    const body = new FormData();
    body.append('file', file);
    read({ body });
  }, [read]);

  const onText = useCallback((text) => {
    if (!String(text || '').trim()) return;
    setPreview((old) => { if (old) URL.revokeObjectURL(old); return null; });
    read({ headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text }) });
  }, [read]);

  /*
   * One change to one row. A change to what the card rate is per, or to the
   * tonnes-per-pallet or pallets-per-load, re-converts the £/pallet from the
   * card's figure (replacing an edit to it).
   */
  const applyPatch = (r, patch) => {
    const next = { ...r, ...patch };
    if ('rate_is' in patch || 't_per_pallet' in patch || 'pallets_per_load' in patch) next.rate = perPallet(next);
    const valid = PREFIX.test(next.postcode) && next.rate !== '' && Number.isFinite(Number(next.rate));
    // Fixing a postcode or rate is taken as wanting the row, once it is valid.
    if (('postcode' in patch || 'rate' in patch) && valid) next.use = true;
    if ('use' in patch) next.use = !!patch.use && valid;
    if (!PREFIX.test(next.postcode)) next.use = false;
    return next;
  };
  const updateRow = useCallback((i, patch) => {
    setRows(rs => rs.map((r, j) => (j === i ? applyPatch(r, patch) : r)));
  }, []);
  const updateRows = useCallback((ids, patch) => {
    const set = new Set(ids);
    setRows(rs => rs.map((r, j) => (set.has(j) ? applyPatch(r, patch) : r)));
  }, []);

  const duplicates = useMemo(() => {
    const count = new Map();
    rows.forEach(r => { if (r.use) count.set(r.postcode, (count.get(r.postcode) || 0) + 1); });
    return new Set([...count].filter(([, n]) => n > 1).map(([p]) => p));
  }, [rows]);

  const chosen = rows.filter(r => r.use);

  const save = async () => {
    if (duplicates.size) { setError('The same postcode is ticked on more than one row.'); return; }
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/supplier-haulage/suppliers/${supplierId}/rates/bulk`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          effective_from: from || today(),
          rows: chosen.map(r => ({
            postcode_prefix: r.postcode,
            rate: Number(r.rate),
            notes: [
              r.printed && r.printed !== r.postcode ? `Card: ${r.printed}` : '',
              // How a per-tonne or per-load rate became £/pallet, so it can be checked later.
              r.rate_is === 'tonne' ? `£${Number(r.rate_read).toFixed(2)}/T × ${r.t_per_pallet} T/pallet` : '',
              r.rate_is === 'load' ? `£${Number(r.rate_read).toFixed(2)}/load ÷ ${r.pallets_per_load} pallets` : '',
              r.note
            ].filter(Boolean).join(' · ') || null
          }))
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not save the rates');
      onSaved?.({ created: data.created || 0, updated: data.updated || 0 });
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Import haulage rates${supplierName ? ` — ${supplierName}` : ''}`}
      size="xl"
      className="haulage-rate-import"
      footer={
        <div className="buttons slideout">
          <div>
            <button data-read-safe className="btn-round btn-danger" onClick={onClose}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          {stage === 'review' && (
            <div>
              <button className="btn-slideout" onClick={() => { setStage('pick'); setResult(null); setRows([]); setError(''); }} disabled={saving}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">restart_alt</span></div>
                <span className="btn-label">Try another</span>
              </button>
            </div>
          )}
          <div>
            <button className="btn-slideout" onClick={save} disabled={stage !== 'review' || !chosen.length || saving}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">save</span></div>
              <span className="btn-label">
                {saving ? 'Saving…' : stage === 'review' ? `Save ${chosen.length} rate${chosen.length === 1 ? '' : 's'}` : 'Save'}
              </span>
            </button>
          </div>
        </div>
      }
    >
      <ReviewBody
        stage={stage}
        error={error}
        preview={preview}
        pasteText={pasteText}
        setPasteText={setPasteText}
        onFile={onFile}
        onText={onText}
        onError={setError}
        result={result}
        rows={rows}
        updateRow={updateRow}
        updateRows={updateRows}
        selected={selected}
        setSelected={setSelected}
        from={from}
        setFrom={setFrom}
        duplicates={duplicates}
      />
    </Modal>
  );
}
